import React from 'react';
import pdfjsLib from 'pdfjs-dist';
import classnames from 'classnames';
import styles from './touchscreen-pdf-viewer.less';
import { Stack, Spinner } from '@servicetitan/design-system';
import { PdfViewerControls } from './pdf-viewer-controls';

pdfjsLib.GlobalWorkerOptions.workerSrc = require('pdfjs-dist/build/pdf');

interface TouchscreenPdfViewerProps {
    src: string;
}

interface TouchscreenPdfViewerState {
    isLoading: boolean;
    pageNumber: number;
    scale: number;
}

export class TouchscreenPdfViewer extends React.Component<
    TouchscreenPdfViewerProps,
    TouchscreenPdfViewerState
> {
    doc?: pdfjsLib.PDFDocumentProxy;

    constructor(props: TouchscreenPdfViewerProps) {
        super(props);
        this.state = {
            isLoading: true,
            pageNumber: 1,
            scale: 1.0,
        };
    }

    componentDidMount() {
        this.init();
    }

    init = async () => {
        const { src } = this.props;
        const loadingTask = pdfjsLib.getDocument(src);
        this.setState({ isLoading: true });
        try {
            this.doc = await loadingTask.promise;
        } catch {
            this.setState({ isLoading: false });
            return;
        }
        this.renderPage(1, 1.5);
    };

    renderPage = async (pageNumber: number, scale: number = 1.5) => {
        this.setState({ pageNumber, scale });
        if (this.doc) {
            const page = await this.doc.getPage(pageNumber);
            const viewport = page.getViewport({ scale });
            const canvas = document.getElementById('pdfViewer');

            if (!canvas) {
                // prevent error when user navigates before render
                return;
            }

            (canvas as any).width = viewport.width;
            (canvas as any).height = viewport.height;
            const canvasContext = (canvas as any).getContext('2d');
            const renderTask = page.render({
                viewport,
                canvasContext,
            });
            renderTask.promise.then(() => {
                this.setState({ isLoading: false });
            });
        }
    };

    handleClickPrevious = () => {
        const { pageNumber } = this.state;
        if (pageNumber > 1) {
            this.renderPage(pageNumber - 1);
        }
    };

    handleClickNext = () => {
        const { pageNumber } = this.state;
        if (this.doc && pageNumber < this.doc.numPages) {
            this.renderPage(pageNumber + 1);
        }
    };

    handleClickZoomOut = () => {
        const { pageNumber, scale } = this.state;
        const newScale = Number((scale * 0.9).toFixed(2));
        this.renderPage(pageNumber, newScale);
    };

    handleClickZoomIn = () => {
        const { pageNumber, scale } = this.state;
        const newScale = Number((scale * 1.1).toFixed(2));
        this.renderPage(pageNumber, newScale);
    };

    render() {
        const { isLoading, pageNumber } = this.state;

        return (
            <>
                {isLoading && (
                    <Stack justifyContent="center" alignItems="center" className="h-100">
                        <Spinner size="large" />
                    </Stack>
                )}
                {!isLoading && (
                    <PdfViewerControls
                        pageNumber={pageNumber}
                        onClickNext={this.handleClickNext}
                        onClickPrevious={this.handleClickPrevious}
                        onClickZoomIn={this.handleClickZoomIn}
                        onClickZoomOut={this.handleClickZoomOut}
                    />
                )}
                <div className={classnames(styles.canvasWrapper, 'h-100')}>
                    <canvas id="pdfViewer" className={styles.canvas} />
                </div>
            </>
        );
    }
}
